import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ScrollText, X } from "lucide-react";
import { api } from "../lib/api";

// Property Authority & Listing Responsibility declaration — required before an
// Individual Host publishes (or edits) a listing. Text/version/labels are
// server-provided; acceptance is recorded server-side against the user AND the
// listing with the declaration + Terms versions and a timestamp. Cancelling
// closes the publishing flow and preserves all form data (nothing is sent).
export default function HostDeclaration({ relationship, relationshipLabel, onAccept, onCancel }) {
  const [doc, setDoc] = useState(null);
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { api.hostDeclaration().then(setDoc).catch((e) => setError(e.message)); }, []);

  const accept = async () => {
    setBusy(true);
    try { await onAccept?.(); } finally { setBusy(false); }
  };

  const relLabel = relationshipLabel
    || doc?.relationships?.find((r) => r.value === relationship)?.label
    || null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
         role="dialog" aria-modal="true" aria-label="Property authority declaration" data-testid="host-declaration-modal">
      <div className="w-full max-w-lg rounded-2xl border border-[#27272A] bg-[#0D1114] p-6 sm:p-7 shadow-2xl shadow-black/60 max-h-[85dvh] flex flex-col">
        <div className="flex items-start justify-between gap-4 shrink-0">
          <div>
            <p className="meta-label !text-[#FF3B45]">Required before publishing</p>
            <h3 className="type-h3 mt-1.5" data-testid="host-declaration-title">
              {doc?.title || "Property Authority & Listing Responsibility"}
            </h3>
          </div>
          <button type="button" onClick={onCancel} aria-label="Close" data-testid="host-declaration-close"
                  className="text-zinc-500 hover:text-white transition-colors shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>
        {relLabel && (
          <p className="mt-2.5 text-xs text-zinc-400 shrink-0" data-testid="host-declaration-relationship">
            Your declared relationship: <span className="text-zinc-100 font-semibold">{relLabel}</span>
          </p>
        )}
        {!doc && !error && <div className="mt-6 h-40 rounded-xl bg-white/[0.03] animate-pulse" />}
        {error && <p className="mt-4 text-sm text-red-400" role="alert" data-testid="host-declaration-error">{error}</p>}
        {doc && (
          <>
            <div className="mt-4 flex-1 min-h-0 overflow-y-auto overscroll-contain rounded-xl border border-[#1F2328] bg-[#080B0D] p-4"
                 data-testid="host-declaration-text" tabIndex={0}>
              {doc.text.split("\n\n").map((p, i) => (
                <p key={i} className="text-sm text-zinc-300 leading-relaxed mb-3 last:mb-0">{p}</p>
              ))}
              <div className="pt-3 border-t border-[#1F2328] flex flex-wrap gap-x-4 gap-y-1.5">
                <Link to="/terms" target="_blank" rel="noreferrer" className="text-xs text-[#FF3B45] underline underline-offset-2 hover:text-white transition-colors">MyRedPark Host Terms</Link>
                <Link to="/legal" target="_blank" rel="noreferrer" className="text-xs text-[#FF3B45] underline underline-offset-2 hover:text-white transition-colors">Legal &amp; Policies</Link>
                <Link to="/privacy" target="_blank" rel="noreferrer" className="text-xs text-[#FF3B45] underline underline-offset-2 hover:text-white transition-colors">Privacy Policy</Link>
              </div>
            </div>
            <p className="mt-3 text-[11px] text-zinc-600 shrink-0" data-testid="host-declaration-version">
              Declaration version {doc.version} · Terms {doc.terms_version}
            </p>
            <label className="mt-4 flex items-start gap-2.5 cursor-pointer shrink-0">
              <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)}
                     data-testid="host-declaration-checkbox" className="mt-0.5 w-4 h-4 accent-[#E50914]" />
              <span className="text-sm text-zinc-200">{doc.checkbox_label}</span>
            </label>
            <div className="mt-5 grid grid-cols-2 gap-3 shrink-0">
              <button type="button" onClick={onCancel} disabled={busy} data-testid="host-declaration-cancel"
                      className="btn-secondary !h-11 text-sm">Cancel</button>
              <button type="button" onClick={accept} disabled={!checked || busy} data-testid="host-declaration-accept"
                      className="btn-primary !h-11 text-sm disabled:opacity-40 disabled:cursor-not-allowed">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScrollText className="w-4 h-4" />}
                Accept &amp; Continue
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
